// COMPANIES THAT LIVE ONLY IN THE CRM — leads the import never had.
//
// WHY THIS EXISTS. The directory is one masonry lead file; Bo's book of
// business is bigger than it. He has emailed 190 company domains and only 46
// of them are in that file, so 144 companies he is actively working were
// invisible to every surface here: the leads page could not list them, the
// follow-up engine dropped their threads ("no row was invented for them"), the
// daily worklist never mentioned them, and initial outreach could not even see
// that they had been contacted. A CRM the product claims to mirror cannot have
// a hundred and forty companies it refuses to show.
//
// So a mirrored company with no directory row becomes a lead in its own right,
// built ONLY from what the mirror actually holds: the company's name, domain,
// owner, lifecycle and rollup, plus the contact addresses the mirror
// associates with it. Nothing is invented — a CRM lead carries no trade, no
// score and no research, and every surface renders those as the blanks they
// are rather than filling them in.
//
// `_crmOnly` marks them so a reader (and a filter) can always tell a lead that
// came from the CRM from one that came from the import.
import { normDomain } from './hubspot.js'

const clean = (v) => String(v ?? '').trim()

/** email -> companyId, inverted into companyId -> [emails]. */
function addressesByCompany(index) {
  const out = new Map()
  const byEmail = (index && index.by_email) || {}
  for (const [email, companyId] of Object.entries(byEmail)) {
    const id = String(companyId || '')
    // '__ambiguous__' means the mirror could not decide which company owns the
    // address; an ambiguous address belongs to no company here.
    if (!id || id.startsWith('__')) continue
    const list = out.get(id)
    if (list) {
      if (list.length < 8) list.push(email)
    } else {
      out.set(id, [email])
    }
  }
  return out
}

/** A stable, collision-proof key for a CRM lead. */
export const crmLeadKey = (companyId) => `hs-${companyId}`

/** Is this row one of these synthesized CRM leads? */
export const isCrmLead = (row) => Boolean(row && row._crmOnly)

/**
 * Every mirrored company that no directory row already covers, as lead rows.
 *
 * @param index      the /hubspot/index payload
 * @param claimedIds company ids the directory already matched — those stay the
 *                   directory's, so a company is never listed twice
 */
export function crmLeadRows(index, claimedIds = new Set()) {
  const companies = (index && index.companies) || {}
  const emails = addressesByCompany(index)
  const rows = []
  for (const [id, company] of Object.entries(companies)) {
    if (!company || claimedIds.has(id)) continue
    const name = clean(company.name)
    if (!name) continue // a company with no name is not a lead anyone can read
    const addrs = emails.get(id) || []
    const rollup = company.rollup && typeof company.rollup === 'object' ? company.rollup : null
    rows.push({
      company_id: crmLeadKey(id),
      name,
      website: clean(company.domain),
      email: addrs[0] || '',
      // Every other address the mirror associates with the company, in the
      // packed format the rest of the app already parses.
      contacts: addrs.slice(1).map((e) => `name=${e.split('@')[0]}; apollo_email=${e}`).join('; '),
      city_state: '',
      trade: '',
      _source: 'hubspot',
      _crmOnly: true,
      _hubspot: {
        matched: true,
        ambiguous: false,
        companyId: id,
        company: {
          name,
          owner_name: clean(company.owner_name),
          domain: clean(company.domain),
          lifecycle: clean(company.lifecycle),
        },
        rollup,
        confidence: 'crm',
      },
    })
  }
  return rows
}

/**
 * The company ids the directory already covers — everything annotateRows
 * matched. Anything outside this set is a CRM-only company.
 */
export function claimedCompanyIds(rows) {
  const ids = new Set()
  for (const row of rows || []) {
    const id = row && row._hubspot && row._hubspot.matched ? String(row._hubspot.companyId || '') : ''
    if (id) ids.add(id)
  }
  return ids
}

/** Domain/address keys a CRM lead answers to, for outreach matching. */
export function crmLeadAddresses(row) {
  const out = new Set()
  const dom = normDomain(row?.website)
  if (dom) out.add(dom)
  for (const e of [row?.email, ...(String(row?.contacts || '').match(/[\w.+-]+@[\w-]+\.[\w.-]+/g) || [])]) {
    const addr = clean(e).toLowerCase()
    if (addr) out.add(addr)
  }
  return out
}
